import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, jsonFetch } from '@/lib/api/jsonFetch'
import type { ApplyResultItem, InspecPo, PreviewItem, ZLot } from './types'

const BASE = '/api/inspec-z1'
const KEY = ['inspec-z1'] as const

export function usePos(includeClosed: boolean) {
  return useQuery({
    queryKey: [...KEY, 'pos', includeClosed],
    queryFn: () =>
      jsonFetch<{ items: InspecPo[]; total: number }>(
        `${BASE}/pos${includeClosed ? '?include_closed=true' : ''}`,
      ),
    staleTime: 30_000,
  })
}

export function useLots(enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, 'lots'],
    queryFn: () => jsonFetch<{ items: ZLot[] }>(`${BASE}/lots`),
    enabled,
    staleTime: 60_000,
  })
}

export function pdfUrl(poNumber: string): string {
  return `${import.meta.env.VITE_API_BASE ?? ''}${BASE}/pos/${encodeURIComponent(poNumber)}/pdf`
}

export function ownWorkbookUrl(): string {
  return `${import.meta.env.VITE_API_BASE ?? ''}${BASE}/workbook.xlsx`
}

function useInvalidate() {
  const qc = useQueryClient()
  return () => void qc.invalidateQueries({ queryKey: KEY })
}

export function usePreview() {
  return useMutation({
    mutationFn: (files: File[]) => {
      const fd = new FormData()
      for (const f of files) fd.append('files', f)
      return jsonFetch<{ items: PreviewItem[] }>(`${BASE}/preview`, { method: 'POST', rawBody: fd })
    },
  })
}

export function useApply() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: (items: { document_id: number; tentative?: boolean | null }[]) =>
      jsonFetch<{ items: ApplyResultItem[] }>(`${BASE}/apply`, { method: 'POST', body: { items } }),
    onSuccess: invalidate,
  })
}

export function useSetLot() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: (vars: { po: string; lot_name: string | null }) =>
      jsonFetch<{ po: InspecPo; odoo: string }>(`${BASE}/pos/${encodeURIComponent(vars.po)}/lot`, {
        method: 'POST',
        body: { lot_name: vars.lot_name },
      }),
    onSuccess: invalidate,
  })
}

export function useSetFlags() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: (vars: {
      po: string
      tentative?: boolean
      cancelled?: boolean
      notes?: string
    }) => {
      const { po, ...body } = vars
      return jsonFetch<InspecPo>(`${BASE}/pos/${encodeURIComponent(po)}`, { method: 'PATCH', body })
    },
    onSuccess: invalidate,
  })
}

export function useSync() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: (po: string) =>
      jsonFetch<{ po: InspecPo; odoo: Record<string, unknown> }>(
        `${BASE}/pos/${encodeURIComponent(po)}/sync`,
        { method: 'POST' },
      ),
    onSuccess: invalidate,
  })
}

export function useUvivaInstruction() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: (po: string) =>
      jsonFetch<{ po: InspecPo; added: string[] }>(
        `${BASE}/pos/${encodeURIComponent(po)}/uviva-instruction`,
        { method: 'POST' },
      ),
    onSuccess: invalidate,
  })
}

export function usePreShipCall() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: (vars: { releaseId: number; note?: string; clear?: boolean }) =>
      jsonFetch<InspecPo>(`${BASE}/releases/${vars.releaseId}/pre-ship-call`, {
        method: 'POST',
        body: { note: vars.note ?? null, clear: vars.clear ?? false },
      }),
    onSuccess: invalidate,
  })
}

export interface WorkbookReport {
  filled: string[]
  missingLot: string[]
  notInSheet: string[]
}

/** Uploads InSpec's spreadsheet and downloads it back with the Assigned
 *  Lot column filled. Raw fetch: the response is a binary workbook. */
export async function fillInspecWorkbook(file: File): Promise<WorkbookReport> {
  const fd = new FormData()
  fd.append('file', file)
  const res = await fetch(`${import.meta.env.VITE_API_BASE ?? ''}${BASE}/workbook`, {
    method: 'POST',
    credentials: 'include',
    body: fd,
  })
  if (!res.ok) {
    let message = `Spreadsheet fill failed (${res.status})`
    try {
      const body = (await res.json()) as { detail?: unknown }
      if (typeof body.detail === 'string') message = body.detail
    } catch {
      // keep the generic message
    }
    throw new ApiError(res.status, message)
  }
  const split = (h: string | null) => (h ? h.split(',').filter(Boolean) : [])
  const report: WorkbookReport = {
    filled: split(res.headers.get('X-Filled')),
    missingLot: split(res.headers.get('X-Missing-Lot')),
    notInSheet: split(res.headers.get('X-Not-In-Sheet')),
  }
  const disposition = res.headers.get('Content-Disposition') ?? ''
  const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'InSpec lots.xlsx'
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = name
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
  return report
}
